import { config } from "../Environment";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface UserTraitSummary {
  _id: string;
  name: string;
  description?: string;
  category: "strength" | "value" | "aspiration" | "skill";
  emoji?: string;
}

export interface IdentitySpace {
  _id: string;
  fiUser: string;
  name: string;
  identityLabel?: string;
  vision?: string;
  focusArea?: string;
  color: string;
  icon: string;
  coverImage?: string;
  status: "created" | "active" | "paused" | "archived";
  traits: UserTraitSummary[];
  isPrimary: boolean;
  sortOrder: number;
  companionEnabled: boolean;
  companionLastRun?: string;
  createdOn: string;
  lastUpdated: string;
  archivedOn?: string;
  pausedOn?: string;
  // PWF Hybrid Bridge — present only on items sourced from the pwfs collection
  __isPwf?: boolean;
  pwfMeta?: {
    fiParent: string | null;
    ancestorIds: string[];
    children: string[];
    aiContext: any | null;
  };
  manifestations?: Array<{ pwfType: string; isActive: boolean; enabledAt: string; config: Record<string, any> }>;
  config?: Record<string, any>;
}

export interface CreateSpaceRequest {
  name: string;
  identityLabel?: string;
  vision?: string;
  focusArea?: string;
  color?: string;
  icon?: string;
  traits?: string[]; // trait names to create alongside the space
}

export interface UpdateSpaceRequest {
  name?: string;
  identityLabel?: string;
  vision?: string;
  focusArea?: string;
  color?: string;
  icon?: string;
  companionEnabled?: boolean;
}

export interface SpaceStats {
  totalActivities: number;
  totalInsights: number;
  totalActions: number;
  pendingActions: number;
  completedActions: number;
  engagementsThisMonth: number;
  traitsCount: number;
  daysSinceCreation: number;
  companionMessagesCount: number;
}

// ─── Space CRUD ──────────────────────────────────────────────────────────────

/**
 * Create a new identity space
 */
export const createSpace = async (
  retryFetch: Function,
  data: CreateSpaceRequest
): Promise<IdentitySpace> => {
  const response = await retryFetch(`${config.API_BASE}/api/identity-spaces`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to create space");
  }

  return response.json();
};

/**
 * List all spaces for the current user (optionally filtered by status)
 */
export const listSpaces = async (
  retryFetch: Function,
  status?: string
): Promise<IdentitySpace[]> => {
  const query = status ? `?status=${status}` : "";
  const response = await retryFetch(
    `${config.API_BASE}/api/identity-spaces${query}`,
    {
      method: "GET",
      credentials: "include",
    }
  );

  if (!response.ok) {
    throw new Error("Failed to fetch spaces");
  }

  return response.json();
};

/**
 * Get a single space by ID (with populated traits)
 */
export const getSpace = async (
  retryFetch: Function,
  id: string
): Promise<IdentitySpace> => {
  const response = await retryFetch(
    `${config.API_BASE}/api/identity-spaces/${id}`,
    {
      method: "GET",
      credentials: "include",
    }
  );

  if (!response.ok) {
    throw new Error("Failed to fetch space");
  }

  return response.json();
};

/**
 * Update a space
 */
export const updateSpace = async (
  retryFetch: Function,
  id: string,
  data: UpdateSpaceRequest
): Promise<IdentitySpace> => {
  const response = await retryFetch(
    `${config.API_BASE}/api/identity-spaces/${id}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to update space");
  }

  return response.json();
};

/**
 * Change space status (active, paused, archived)
 */
export const changeSpaceStatus = async (
  retryFetch: Function,
  id: string,
  status: string
): Promise<IdentitySpace> => {
  const response = await retryFetch(
    `${config.API_BASE}/api/identity-spaces/${id}/status`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ status }),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to change space status");
  }

  return response.json();
};

/**
 * Set a space as the primary (active) space
 */
export const setPrimarySpace = async (
  retryFetch: Function,
  id: string
): Promise<IdentitySpace> => {
  const response = await retryFetch(
    `${config.API_BASE}/api/identity-spaces/${id}/set-primary`,
    {
      method: "PUT",
      credentials: "include",
    }
  );

  if (!response.ok) {
    throw new Error("Failed to set primary space");
  }

  return response.json();
};

/**
 * Reorder spaces
 */
export const reorderSpaces = async (
  retryFetch: Function,
  spaceIds: string[]
): Promise<void> => {
  const response = await retryFetch(
    `${config.API_BASE}/api/identity-spaces/reorder`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ spaceIds }),
    }
  );

  if (!response.ok) {
    throw new Error("Failed to reorder spaces");
  }
};

/**
 * Archive (soft-delete) a space
 */
export const archiveSpace = async (
  retryFetch: Function,
  id: string
): Promise<void> => {
  const response = await retryFetch(
    `${config.API_BASE}/api/identity-spaces/${id}`,
    {
      method: "DELETE",
      credentials: "include",
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to archive space");
  }
};

/**
 * Get the currently active space (primary or most recently updated)
 */
export const getActiveSpace = async (
  retryFetch: Function
): Promise<IdentitySpace | null> => {
  const response = await retryFetch(
    `${config.API_BASE}/api/identity-spaces/active`,
    {
      method: "GET",
      credentials: "include",
    }
  );

  if (!response.ok) {
    throw new Error("Failed to fetch active space");
  }

  const data = await response.json();
  return data || null;
};

/**
 * Get stats for a specific space
 */
export const getSpaceStats = async (
  retryFetch: Function,
  id: string
): Promise<SpaceStats> => {
  const response = await retryFetch(
    `${config.API_BASE}/api/identity-spaces/${id}/stats`,
    {
      method: "GET",
      credentials: "include",
    }
  );

  if (!response.ok) {
    throw new Error("Failed to fetch space stats");
  }

  return response.json();
};

// ─── Space Activities ────────────────────────────────────────────────────────

export interface SpaceActivity {
  prescriptionId: string;
  prescriptionName: string;
  prescriptionDescription?: string;
  processId?: string;
  processStatus?: string;
  lastUpdated?: number;
  completedCount: number;
  reminderSettings?: {
    enabled: boolean;
    frequency: string;
    time?: string;
  };
  spaceIds?: string[];
}

/**
 * Fetch activities linked to a space (with process status + reminders)
 */
export const fetchSpaceActivities = async (
  retryFetch: Function,
  spaceId: string
): Promise<SpaceActivity[]> => {
  const response = await retryFetch(
    `${config.API_BASE}/api/identity-spaces/${spaceId}/activities`,
    { method: "GET", credentials: "include" }
  );

  if (!response.ok) {
    throw new Error("Failed to fetch space activities");
  }

  return response.json();
};

/**
 * Link/unlink an activity to identity spaces by updating its spaceIds
 */
export const updateActivitySpaces = async (
  retryFetch: Function,
  prescriptionId: string,
  spaceIds: string[]
): Promise<any> => {
  const response = await retryFetch(
    `${config.API_BASE}/api/user-activity-metadata/${prescriptionId}/spaces`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ spaceIds }),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to update activity spaces");
  }

  return response.json();
};

// ─── View Mode ───────────────────────────────────────────────────────────────

/**
 * Get the user's current view mode preference
 */
export const getViewMode = async (
  retryFetch: Function
): Promise<{ mode: string; activeSpaceId?: string }> => {
  const response = await retryFetch(`${config.API_BASE}/api/user/view-mode`, {
    method: "GET",
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Failed to fetch view mode");
  }

  return response.json();
};

/**
 * Set the user's view mode preference
 */
/**
 * Ask AI to suggest an icon based on the space name and identity label
 */
export const suggestSpaceIcon = async (
  retryFetch: Function,
  data: { identityLabel: string; spaceName: string }
): Promise<{ icon: string }> => {
  const response = await retryFetch(
    `${config.API_BASE}/api/identity-spaces/suggest-icon`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(data),
    }
  );
  if (!response.ok) throw new Error("Failed to suggest icon");
  return response.json();
};

export const setViewMode = async (
  retryFetch: Function,
  mode: string,
  activeSpaceId?: string
): Promise<void> => {
  const response = await retryFetch(`${config.API_BASE}/api/user/view-mode`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ mode, activeSpaceId }),
  });

  if (!response.ok) {
    throw new Error("Failed to set view mode");
  }
};
